import { useSearchParams } from "@remix-run/react";
import clsx from "clsx";

import { EditableContent } from "~/components/editable-content";
import { useIsMobile } from "~/hooks/use-mobile";
import { useSession, useSessionMutation } from "~/hooks/use-session";

export const TitleEditor: React.FC<{
    sessionId: string;
    currentTitle: string | null;
    /** Overrides the default size so callers can render it at H2 scale. */
    className?: string;
}> = ({ sessionId, currentTitle, className }) => {
    const isMobile = useIsMobile();
    const { session, isConsultantViewer } = useSession(sessionId);
    const [searchParams] = useSearchParams();
    const sessionToken = searchParams.get("sessionToken");
    // AIDEV-NOTE: This component is self-contained (session-notes.tsx and
    // SessionPageHeader both render it with just sessionId/currentTitle), so
    // it must detect a consultant viewer itself rather than trust a caller to
    // pass readOnly — a caller-level flag is exactly what got missed here
    // before (Adam, 2026-09-03: title stayed editable for a consultant even
    // after session-notes.tsx's own isReadOnlyViewer fix, because this
    // component never consulted it).
    const isPublicSession = !!sessionToken || isConsultantViewer;
    const { mutate: updateSession, isPending } = useSessionMutation(
        sessionId,
        session?.uploadedByUserId,
        sessionToken,
    );

    const handleSubmit = async (newTitle: string) => {
        updateSession({
            action: "update-title",
            title: newTitle,
        });
        return Promise.resolve();
    };

    return (
        <div className={clsx("w-full")} data-testid="title-editor">
            <EditableContent
                defaultValue={currentTitle || ""}
                onSubmit={handleSubmit}
                className={clsx(
                    "font-semibold font-display text-brand-forest-800 whitespace-pre-wrap text-left not-prose leading-[1.3]",
                    isMobile ? "text-3xl" : "text-3xl",
                    "print:text-[20px]",
                    className,
                )}
                disabled={isPublicSession}
            />
        </div>
    );
};
